import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/Preloader.css';
import CustomButtonDelete from '../CustomElements/CustomButtonDelete';
import CustomButtonEdit from '../CustomElements/CustomButtonEdit';
import CustomButton from '../CustomElements/CustomButton';
import Table from '../Table/Table';
import Modal from '../Modal/Modal';
import AddOrEditSemaphore from './AddOrEditSemaphore';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomTitle from '../CustomElements/CustomTitle';
import { useInfocard } from './Common';
// Кастомные хуки
import { useRole } from '../../customHook/useRole';
// Утилиты
import { defaultCellRender } from '../../utils/formatValueForHTML';
// Действия Redux
import { deleteSemaphoreFromServer } from '../../redux/actions/infocards.action';
import { requestSemaphores } from '../../redux/actions/infocards.action';
import { load, remove } from '../../redux/actions/loader.action';

// Используется для отображения и управления светофорами на станции

export default function Semaphores({ t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  //получаем id текущей станции и дороги
  const { stationId, road } = useInfocard();
  // роли пользователя
  const isAdmin = useRole('admin');
  // получаем все светофоры из стейта
  const semaphores = useSelector((s) => s.infocards.semaphores);
  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);
  // состояние для открытия модалки
  const [openModal, setOpenModal] = useState(false);
  // состояние для отображения страницы подтверждения
  const [addSuccess, setAddSuccess] = useState(false);
  // состояние для открытия модалки добавления или редактирования светофора
  const [addOrEditSemaphore, setAddOrEditSemaphore] = useState(false);
  // состояние для открытия модалки подтверждения удаления
  const [deleteSemaphore, setDeleteSemaphore] = useState(false);
  // состояние для текущего светофора
  const [currentSemaphore, setCurrentSemaphore] = useState();
  // состояние флага если мы в режиме редактирования
  const [isEdit, setIsEdit] = useState(false);

  // Закрыть модальное окно
  const closeModal = () => {
    setAddOrEditSemaphore(false);
    setDeleteSemaphore(false);
    setCurrentSemaphore();
    setAddSuccess(false);
    setOpenModal(false);
  };

  // удаление светофора
  const removeSemaphore = () => {
    if (isAdmin) {
      dispatch(load());
      dispatch(deleteSemaphoreFromServer(currentSemaphore?.semaphoreId, currentSemaphore?.stationId));
    }
  };

  // запрашиваем все светофоры если есть id станции
  useEffect(() => {
    if (stationId) {
      dispatch(requestSemaphores(stationId));
    }
  }, [stationId, dispatch]);

  // формируем отображаемое имя, если id станции не совпадает с id станции РЦ добавляем имя станции в имя
  const formatRailChain = (name, stationId, stationName, originalStationId) => {
    const stationMismatch = stationId && stationId !== originalStationId;
    return stationMismatch ? `${stationName}.${name}` : name;
  };

  // колонки для таблицы
  const columns = useMemo(
    () =>
      [
        {
          Header: t?.semaphoreId || 'ID светофора',
          accessor: 'semaphoreId',
          Cell: ({ value }) => defaultCellRender(value),
        },
        {
          Header: t?.semaphore || 'Светофор',
          accessor: 'name',
          Cell: ({ value }) => defaultCellRender(value),
        },
        {
          Header: t?.directionSemaphore || 'Направление движения по светофору',
          accessor: 'direction',
          Cell: ({ cell: { value } }) => (
            <span> {value === '0' ? t?.even || 'четное' : value === '1' ? t?.odd || 'нечетное' : '-'}</span>
          ),
        },
        {
          Header: t?.railBefore || 'РЦ перед светофором',
          accessor: 'railChainBeforeName',
          Cell: ({ row: { original } }) =>
            defaultCellRender(
              formatRailChain(
                original?.railChainBeforeName,
                original?.railChainBeforeStation,
                original.railChainBeforeStationName,
                original.stationId
              )
            ),
        },
        {
          Header: t?.railAfter || 'РЦ за светофором',
          accessor: 'railChainAfterName',
          Cell: ({ row: { original } }) =>
            defaultCellRender(
              formatRailChain(
                original?.railChainAfterName,
                original?.railChainAfterStation,
                original.railChainAfterStationName,
                original.stationId
              )
            ),
        },
        isAdmin && {
          Header: t?.actions || 'Действия',
          accessor: '.',
          Cell: ({ row: { original } }) => (
            <>
              <CustomButtonEdit
                onClick={() => {
                  setCurrentSemaphore(original);
                  setIsEdit(true);
                  setAddOrEditSemaphore(true);
                  setOpenModal(true);
                }}
              />
              <CustomButtonDelete
                onClick={() => {
                  setCurrentSemaphore(original);
                  setDeleteSemaphore(true);
                  setOpenModal(true);
                }}
              />
            </>
          ),
        },
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [semaphores, isAdmin, t]
  );

  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess && deleteSemaphore) {
      closeModal();
    }
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess]);

  return (
    <div className="d-flex flex-column h-100 container-fluid overflow-hidden flex-nowrap">
      <Table columns={columns} data={semaphores} hiddenDiv={true} />
      <div className="d-flex flex-row flex-nowrap justify-content-evenly">
        {isAdmin && (
          <CustomButton
            className="confirmation-btn"
            text={ `+ ${t?.addSemaphore}` || '+ Добавить светофор'}
            isButton
            onClick={() => {
              setIsEdit(false);
              setAddOrEditSemaphore(true);
              setOpenModal(true);
            }}
          />
        )}
      </div>
      {openModal && (
        <Modal isOpen={openModal} onClose={closeModal} containerSelector="#infocard-container">
          {addOrEditSemaphore && (
            <AddOrEditSemaphore
              closeModal={closeModal}
              stationId={stationId}
              road={road}
              isEdit={isEdit}
              currentSemaphore={currentSemaphore}
              addSuccess={addSuccess}
              setAddSuccess={setAddSuccess}
              isAdmin={isAdmin}
              t={t}
            />
          )}
          {deleteSemaphore && (
            <CustomContainer>
              <CustomTitle titleText={t?.titleDeleteSemaphore || 'УДАЛЕНИЕ СВЕТОФОРА'} />
              <ConfirmationPage
                onConfirm={removeSemaphore}
                onCancel={closeModal}
                confirmButtonText={t?.confirm || 'Подтвердить'}
                cancelButtonText={t?.cancel || 'Отмена'}
                confirmText={`${t?.confirmDeleteSemaphore || 'Вы уверены, что хотите удалить светофор'} ${
                  currentSemaphore?.semaphoreId
                }?`}
                btnWidth="var(--sadr-font-size-420)"
              />
            </CustomContainer>
          )}
        </Modal>
      )}
    </div>
  );
}
